import { cn } from "@istari-frontend/shared";
import { cva, type VariantProps } from "class-variance-authority";
import { Slot as SlotPrimitive } from "radix-ui";
import * as React from "react";
import { useId } from "react";

import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";

export const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap border rounded-md font-medium transition-all cursor-pointer disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive active:scale-98 active:[&_svg]:scale-98",
  {
    variants: {
      variant: {
        default: "border-primary bg-primary text-primary-foreground shadow-xs hover:bg-primary/85",
        destructive:
          "border-destructive bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border-border bg-background shadow-xs hover:bg-accent/60 hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        secondary: "border-secondary bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/40",
        ghost: "border-transparent hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "border-transparent text-primary underline-offset-4 hover:text-foreground hover:underline",
      },
      size: {
        default: "h-9 gap-2 px-4 py-2 has-[>svg]:px-3 text-sm",
        lg: "h-10 rounded-md gap-2 px-6 has-[>svg]:px-4 text-sm",
        sm: "h-8 rounded-sm gap-1.5 px-3 has-[>svg]:px-2.5 text-sm ",
        xs: "h-7 rounded-sm gap-1.5 px-2.5 has-[>svg]:px-2 text-xs",
        "2xs": "h-6 rounded gap-0.5 px-2 has-[>svg]:px-1.5 text-[0.75rem]",
        icon: "size-9 rounded-md",
        "icon-sm": "size-8 rounded-md",
        "icon-xs": "size-7 rounded-sm ",
        "icon-2xs": "size-6 rounded [&_svg]:size-3",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export const Button = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> &
    VariantProps<typeof buttonVariants> & {
      asChild?: boolean;
      tooltip?: string;
    }
>(({ className, variant, size, asChild = false, tooltip, ...props }, ref) => {
  const Comp = asChild ? SlotPrimitive.Slot : "button";
  const tooltipId = useId();

  const buttonElement = (
    <Comp
      ref={ref}
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      aria-describedby={tooltip ? tooltipId : undefined}
      {...props}
    />
  );

  if (tooltip) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{buttonElement}</TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    );
  }

  return buttonElement;
});

Button.displayName = "Button";
